import { useBackendSubscription } from '@/hooks/useBackendSubscription';
import { Crown, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';

interface FeatureGateProps {
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
  showUpgrade?: boolean;
}

export function FeatureGate({
  feature,
  children,
  fallback,
  showUpgrade = true,
}: FeatureGateProps) {
  const { isPro, isPremium, isActive, isLoading, isFetching } =
    useBackendSubscription();

  // Check if user has access to the feature
  const hasAccess = (() => {
    switch (feature) {
      case 'pro':
        return isPro || isPremium;
      case 'premium':
        return isPremium;
      case 'quickbooks':
        return isPro || isPremium;
      default:
        return false;
    }
  })();

  // Show loading state or fallback while subscription is being fetched/refetched
  // This prevents showing upgrade prompt prematurely when data is loading or being refreshed
  // Only show fallback if we're in initial load, otherwise show children to avoid flicker
  if (isLoading) {
    return <>{fallback || null}</>;
  }

  // If we're fetching (refreshing) but already have data, show children if access is granted
  // This prevents flicker when refetching after subscription update
  if (isFetching && hasAccess) {
    return <>{children}</>;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showUpgrade) {
    return null;
  }

  return <UpgradePrompt feature={feature} />;
}

interface UpgradePromptProps {
  feature: string;
}

function UpgradePrompt({ feature }: UpgradePromptProps) {
  const getFeatureInfo = (feature: string) => {
    switch (feature) {
      case 'pro':
        return {
          title: 'Upgrade to Pro',
          description: 'Unlock advanced features for growing portfolios',
          features: [
            'Up to 25 assets',
            '7 years transaction history',
            '10GB document storage',
            'Auto-weekly bank sync',
            'Google Drive integration',
          ],
        };
      case 'premium':
        return {
          title: 'Upgrade to Premium',
          description: 'Get the full experience for serious investors',
          features: [
            'Up to 100 assets',
            'Unlimited transaction history',
            '100GB document storage',
            'Advanced analytics',
            'Priority support',
          ],
        };
      case 'quickbooks':
        return {
          title: 'Upgrade to Pro',
          description: 'QuickBooks integration requires a Pro or Premium plan',
          features: [
            'Sync QuickBooks accounts and transactions',
            'Map transactions to your assets',
            'Automatic transaction import',
            'All Pro plan features included',
          ],
        };
      default:
        return {
          title: 'Upgrade Required',
          description: 'This feature requires a paid plan',
          features: [],
        };
    }
  };

  const featureInfo = getFeatureInfo(feature);

  return (
    <Card className="border-amber-200 bg-amber-50">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
          <Lock className="h-6 w-6 text-amber-600" />
        </div>
        <CardTitle className="text-lg">{featureInfo.title}</CardTitle>
        <CardDescription>{featureInfo.description}</CardDescription>
      </CardHeader>
      <CardContent className="text-center">
        {featureInfo.features.length > 0 && (
          <ul className="mb-4 space-y-2 text-sm text-gray-600">
            {featureInfo.features.map((feature, index) => (
              <li key={index} className="flex items-center justify-center">
                <span className="mr-2">✓</span>
                {feature}
              </li>
            ))}
          </ul>
        )}
        <Button
          className="w-full"
          onClick={() => {
            // This will redirect to Stripe subscription management
            window.location.href = '/app/settings';
          }}
        >
          <Crown className="mr-2 h-4 w-4" />
          Upgrade Now
        </Button>
      </CardContent>
    </Card>
  );
}

// Convenience components for common features
export function ProFeature({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <FeatureGate feature="pro" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}

export function PremiumFeature({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <FeatureGate feature="premium" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}

export function QuickBooksFeature({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return (
    <FeatureGate feature="quickbooks" fallback={fallback}>
      {children}
    </FeatureGate>
  );
}
